import Upsertable from '@/models/Upsertable'
import { useCustomerStore } from '@/stores/customerStore'
import Demographic from './Demographic'
import Farm from './Farm'
import Field from './Field'
import Economic from './Economic'
import Harvest from './Harvest'
import Assessment from './Assessment'
import { useModelName } from '@/composables/useModelName.js'
import { ModelRegistry } from '@/models/modelRegistry.js'

export default class Customer extends Upsertable {
  static store() {
    return useCustomerStore()
  }

  store() {
    return Customer.store()
  }

  get_name() {
    let nameParts = []

    if (this.first_name) nameParts.push(this.first_name)
    if (this.last_name) nameParts.push(this.last_name)
    if (this.first_surname) nameParts.push(this.first_surname)

    // Use this.names if no name parts are available
    if (nameParts.length === 0 && this.names) {
      return this.names
    }

    return nameParts.join(' ')
  }


  // From September to October 2026 the Existing Farmer form saved a CPC or WinWin farmer's type as
  // farmer_type, which made the contract screen ask for a company. Moves such a value back to
  // customer_type, and leaves the farmer private unless registered as an association or a company.
  restoreCustomerType(customerTypes) {
    if (!this.customer_type && customerTypes.includes(this.farmer_type)) {
      this.customer_type = this.farmer_type
    }
    if (!['association', 'company'].includes(this.farmer_type)) {
      this.farmer_type = 'private'
    }
  }

  // The field app keeps a farmer's names as first_name and last_name. The back end's profile keeps the
  // family name as surname, and a bulk upload keeps only the whole name, as name. An existing farmer's
  // names are shown and saved as first_name and last_name, as a new farmer's are. A surname moves to
  // last_name rather than being copied, as keeping both would name the farmer twice.
  useFirstAndLastName() {
    if (!this.last_name && this.surname) {
      this.last_name = this.surname
      delete this.surname
    }

    // The whole name only when there is no other: its last word is the family name. A farmer with no
    // name at all is named by their phone number or grower number instead, which is not split.
    const name = String(this.name ?? '').trim()
    const isName = /\p{L}/u.test(name) && name !== this.partner_identifier
    const words = isName ? name.split(/\s+/) : []
    if (!this.first_name && !this.last_name && words.length > 0) {
      this.last_name = words.length > 1 ? words.pop() : ''
      this.first_name = words.join(' ')
    }
  }

  demographic() {
    let demographic = Demographic.store().findByCustomerUuid(this.uuid)
    demographic.geographic ||= []
    return Object.assign(new Demographic(), demographic)
  }

  farm() {
    let farm = Farm.store().findByCustomerUuid(this.uuid)
    return Object.assign(new Farm(), farm)
  }

  farms() {
    let farms = Farm.store().findAllByCustomerUuid(this.uuid)
    let farmObjects = []
    farms.forEach(function (item) {
      farmObjects.push(Object.assign(new Farm(), item))
    })
    return farmObjects
  }

  fields(){
    let fields = Field.store().findAllByCustomerUuid(this.uuid)
    let fieldObjects = []
    fields.forEach(function (item) {
      fieldObjects.push(Object.assign(new Field(), item))
    })
    return fieldObjects
  }
  

  economic() {
    let economic = Economic.store().findByCustomerUuid(this.uuid)
    return Object.assign(new Economic(), economic)
  }

  harvests() {
    let harvests = Harvest.store().findAllByCustomerUuid(this.uuid)
    let harvestObjects = []
    harvests.forEach(function (item) {
      harvestObjects.push(Object.assign(new Harvest(), item))
    })
    return harvestObjects
  }

  latestHarvest() {
    let harvests = this.harvests()
    if (harvests == []) {
      return null
    } else {
      harvests.sort(function (a, b) {
        if (a.season_name > b.season_name) {
          return -1
        }
        if (a.season_name < b.season_name) {
          return 1
        }
        return 0
      })
      return harvests[0]
    }
  }

  assessments() {
    let assessments = Assessment.store().findAllByCustomerUuid(this.uuid)
    let assessmentObjects = []
    assessments.forEach(function (item) {
      assessmentObjects.push(Object.assign(new Assessment(), item))
    })
    return assessmentObjects
  }

  beforeSave() {
    //Update name cache , etc
    this.name_cache = this.get_name()
  }

  afterSave() {
    //Update name cache , etc
  }

  delete() {}
}

useModelName(Customer, "Customer")
ModelRegistry.register(Customer, "Customer")